// Script placeholders, voicemail and email selection. Pure; unit-tested.
// Real-data rule (SPEC §0.6): {observation} is filled only from a setter-verified observation; a missing value is never
// invented, it is shown as a highlighted gap so the setter adapts the line.
import { CALENDLY_ACCOUNT_URL, selfBookingSlug } from '../../../src/shared/calendly';
import type { Business, CampaignScript, EmailTemplate, ScriptBlock, VmRecording, VoicemailScript } from '../../../src/shared/api';

export type TemplateVars = Partial<Record<string, string | null | undefined>>;
export interface Segment { text: string; missing?: string }

const PLACEHOLDER = /\{([a-z_]+)\}/g;

/** Splits a template into literal text and filled placeholders; unknown or empty values become `missing` segments. */
export function fillSegments(template: string, vars: TemplateVars): Segment[] {
  const out: Segment[] = [];
  let last = 0;
  for (const m of template.matchAll(PLACEHOLDER)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push({ text: template.slice(last, idx) });
    const value = vars[m[1]];
    if (value !== undefined && value !== null && value.trim() !== '') out.push({ text: value });
    else out.push({ text: `[${m[1].replace(/_/g, ' ')}]`, missing: m[1] });
    last = idx + m[0].length;
  }
  if (last < template.length) out.push({ text: template.slice(last) });
  return out;
}

export function fillText(template: string, vars: TemplateVars): string {
  return fillSegments(template, vars).map((s) => s.text).join('');
}

export function placeholdersIn(template: string): string[] {
  return [...template.matchAll(PLACEHOLDER)].map((m) => m[1]);
}

export function firstName(full: string | null | undefined): string | null {
  const f = (full ?? '').trim().split(/\s+/)[0];
  return f ? f : null;
}

/**
 * What {setter} says on this lead. US businesses get the setter's full name as stored on the user: South Carolina,
 * Virginia and North Dakota require the caller's first AND last name (S.C. Code 37-21-40, Va. Code 59.1-512,
 * N.D.C.C. 51-28-07), and the Default opener carries it. Canadian businesses keep the first name.
 */
export function setterScriptName(full: string | null | undefined, country: string | null | undefined): string | null {
  if (country !== 'US') return firstName(full);
  const name = (full ?? '').trim().replace(/\s+/g, ' ');
  return name ? name : null;
}

/** The verified observation, or null. An AI-drafted observation stays out of the script until a setter verifies it. */
export function verifiedObservation(b: Pick<Business, 'observation' | 'observationVerifiedAt'>): string | null {
  return b.observation && b.observationVerifiedAt ? b.observation : null;
}

export function scriptVars(args: {
  setterName: string; business: Business; dmName?: string | null; callback?: string | null; calendlyLink?: string | null;
  mailingAddress?: string | null;
}): TemplateVars {
  const dm = args.dmName ?? args.business.dmName;
  return {
    setter: setterScriptName(args.setterName, args.business.country),
    business: args.business.name,
    dm_name: dm,
    first_name: firstName(dm),
    gatekeeper_name: args.business.gatekeeperName,
    observation: verifiedObservation(args.business),
    callback: args.callback ?? null,
    calendly_link: args.calendlyLink ?? null,
    city: args.business.city,
    mailing_address: args.mailingAddress?.trim() || null,
  };
}

/**
 * Openers to show: with a verified observation, the observation openers first; without one, only the openers that
 * don't use {observation} (SPEC §15.1). `withoutObservationFallback` is true when every opener needs an observation.
 */
export function pickOpeners(openers: ScriptBlock[], hasVerifiedObservation: boolean): { openers: ScriptBlock[]; allNeedObservation: boolean } {
  const uses = (o: ScriptBlock) => placeholdersIn(o.text).includes('observation');
  if (hasVerifiedObservation) return { openers: [...openers.filter(uses), ...openers.filter((o) => !uses(o))], allNeedObservation: false };
  const free = openers.filter((o) => !uses(o));
  return { openers: free, allNeedObservation: free.length === 0 && openers.length > 0 };
}

/** An opener that fills on every lead (only {setter} and {business}): the opener A/B rotation pool. */
export function isEveryLeadOpener(o: ScriptBlock): boolean {
  return placeholdersIn(o.text).every((n) => n === 'setter' || n === 'business');
}

export interface ShownOpener {
  opener: ScriptBlock | null;     // the one opener on screen (REVIEW-2026-10-01: one at a time)
  choices: ScriptBlock[];         // what the small A/B selector offers, in order (pickOpeners)
  how: 'chosen' | 'observation' | 'rotation' | 'only' | 'none';
  allNeedObservation: boolean;
}

/**
 * The opener on screen for this lead. `chosen` (the label the setter picked in the selector) wins while it is offered.
 * Otherwise a verified-observation opener comes first, as before. Otherwise the openers that fill on every lead rotate by
 * lead id (membershipId mod n), so each variant gets about half the leads and nobody picks by mood: the label goes to
 * /api/calls/prepare as openerVariant and is stored on the call (opener A/B).
 */
export function shownOpener(openers: ScriptBlock[], opts: { hasObservation: boolean; membershipId: number; chosen?: string | null }): ShownOpener {
  const picked = pickOpeners(openers, opts.hasObservation);
  const choices = picked.openers;
  const base = { choices, allNeedObservation: picked.allNeedObservation };
  if (!choices.length) return { ...base, opener: null, how: 'none' };
  const chosen = opts.chosen ? choices.find((o) => o.label === opts.chosen) : undefined;
  if (chosen) return { ...base, opener: chosen, how: 'chosen' };
  if (opts.hasObservation && placeholdersIn(choices[0].text).includes('observation')) return { ...base, opener: choices[0], how: 'observation' };
  const pool = choices.filter(isEveryLeadOpener);
  if (pool.length > 1) {
    const i = Math.abs(Math.trunc(opts.membershipId)) % pool.length;
    return { ...base, opener: pool[i], how: 'rotation' };
  }
  return { ...base, opener: pool[0] ?? choices[0], how: 'only' };
}

/** The selector's letter for choice `index`: A, B, C… */
export function openerLetter(index: number): string {
  return String.fromCharCode(65 + Math.max(0, Math.min(25, index)));
}

/** REVIEW-2026-10-01: the dial screen shows three "never say" rules; the rest stay in the guide, playbook and training. */
export const ON_SCREEN_RULE_COUNT = 3;

/** The campaign's on-screen rules: its first three compliance notes (templates put the three that matter most first). */
export function onScreenRules(notes: readonly string[] | null | undefined): string[] {
  return (notes ?? []).filter((n) => typeof n === 'string' && n.trim()).slice(0, ON_SCREEN_RULE_COUNT);
}

/** VM1 for the first voicemail, VM2 after that (playbook: VM1 ~15 s context, VM2 ~30 s context + proof). */
export function pickLiveVoicemail(scripts: VoicemailScript[], vmCount: number): VoicemailScript | null {
  if (scripts.length === 0) return null;
  const want = vmCount === 0 ? 'first' : 'second';
  return scripts.find((s) => s.situation === want) ?? scripts[Math.min(vmCount, scripts.length - 1)];
}

/**
 * The approved recording to drop: mine, human-voice attested, for this campaign (or any campaign), matching the attempt
 * (first/second) when possible; campaign-specific beats generic; newest wins.
 */
export function pickDropRecording(recordings: VmRecording[], args: { userId: number; campaignId: number; vmCount: number }): VmRecording | null {
  const usable = recordings.filter((r) => r.userId === args.userId && r.status === 'approved' && r.humanVoiceAttested
    && (r.campaignId === null || r.campaignId === args.campaignId));
  if (usable.length === 0) return null;
  const want = args.vmCount === 0 ? 'first' : 'second';
  const score = (r: VmRecording) => (r.situation === want ? 2 : 0) + (r.campaignId === args.campaignId ? 1 : 0);
  return [...usable].sort((a, b) => score(b) - score(a) || b.createdAt.localeCompare(a.createdAt))[0];
}

/** Calendly setter link (CALENDLY_ACCOUNT_URL/<slug>, src/shared/calendly.ts), tagged so bookings from follow-up email
 *  (or a text: 'sms', the composer's "Insert booking link", 2026-10-03) are attributable. The link follows the shared
 *  rule (selfBookingSlug): the campaign's link; a cannabis business /your-store; no campaign Your growth call.
 *  `cannabis` undefined = not known here: the campaign's own link is used as it is. */
export function calendlyLink(slug: string | null, setterFirst: string | null, source: 'email' | 'phone' | 'sms' = 'email', cannabis?: boolean): string {
  const s = selfBookingSlug(slug, cannabis);
  const who = encodeURIComponent((setterFirst ?? 'setter').toLowerCase());
  return `${CALENDLY_ACCOUNT_URL}/${encodeURIComponent(s)}?utm_source=${source}&utm_medium=setter&utm_campaign=${who}`;
}

/**
 * A plain business address that is safe to put in a `mailto:` link. Stricter than RFC 5322 on purpose: the local part
 * may not contain `? & = % / \ #`, quotes, spaces or brackets, because in a URL those start a query (`?bcc=…` would add
 * hidden recipients), a fragment or an escape. The domain is LDH labels only (punycode for IDNs).
 */
const SAFE_EMAIL = /^[A-Za-z0-9.!$'*+^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+$/;

export function isSafeEmail(email: string): boolean {
  return email.length <= 254 && SAFE_EMAIL.test(email);
}

/** `mailto:` target: every character percent-encoded except the `@` (RFC 6068 allows it literally; clients expect it). */
export function mailtoAddress(email: string): string {
  return encodeURIComponent(email).replace(/%40/g, '@');
}

export const MAILING_ADDRESS_MISSING = 'Add the company mailing address in Settings first (CASL / CAN-SPAM).';

export interface EmailFollowUp { ok: boolean; reason: string | null; href: string | null; subject: string; to: string | null }

/**
 * Builds the `mailto:` follow-up from the campaign's template for `situation` ('after_vm' | 'after_call').
 * Blocked (with the reason shown to the setter) when: no email on file; the address isn't a plain safe one; Canadian
 * business without an email source URL (CASL s.10(9)(b): only a conspicuously published address); no company mailing
 * address in Settings (`vars.mailing_address`: CASL s.6(2)(b) and CAN-SPAM need a postal address in every commercial
 * email); the template needs a verified observation we don't have. A template that forgot `{mailing_address}` still
 * gets the address appended, so no email leaves without it.
 */
export function buildFollowUpEmail(script: CampaignScript | null, situation: string, business: Business, vars: TemplateVars): EmailFollowUp {
  const tpl: EmailTemplate | undefined = script?.emails?.find((e) => e.situation === situation);
  const none = (reason: string): EmailFollowUp => ({ ok: false, reason, href: null, subject: tpl?.subject ?? '', to: business.email });
  if (!tpl) return none(`This campaign has no "${situation.replace('_', ' ')}" email template.`);
  if (!business.email) return none('No email address on file for this business.');
  if (!isSafeEmail(business.email)) return none(`The email on file ("${business.email}") doesn't look valid. Fix it in Leads first.`);
  if (business.country === 'CA' && !business.emailSourceUrl) {
    return none("Canada (CASL): we can only email an address we found published, and this one has no source URL. Add the source in Leads first.");
  }
  const address = vars.mailing_address?.trim();
  if (!address) return none(MAILING_ADDRESS_MISSING);
  const needsObservation = placeholdersIn(tpl.subject + ' ' + tpl.body).includes('observation');
  if (needsObservation && !vars.observation) return none('This template quotes an observation, and none is verified for this business yet.');
  const emailVars: TemplateVars = { ...vars, first_name: vars.first_name || 'there', mailing_address: address };
  const subject = fillText(tpl.subject, emailVars);
  let body = fillText(tpl.body, emailVars);
  if (!placeholdersIn(tpl.body).includes('mailing_address')) body = `${body.trimEnd()}

${address}`;
  const href = `mailto:${mailtoAddress(business.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return { ok: true, reason: null, href, subject, to: business.email };
}

/**
 * The follow-up email button as the wrap-up dock shows it. While the company settings are still loading, or failed to
 * load, a missing mailing address means "we don't know yet", not "nobody set it": the reason says so instead of sending
 * the setter to Settings. Every other outcome of buildFollowUpEmail passes through unchanged.
 */
export function followUpEmailButton(built: EmailFollowUp | null, settings: { loaded: boolean; error: string | null }): EmailFollowUp | null {
  if (!built || built.ok || built.reason !== MAILING_ADDRESS_MISSING || settings.loaded) return built;
  return { ...built, reason: settings.error ?? 'Loading the company mailing address…' };
}
